import type {Participant, ParticipationInterval, Raid} from "@/raids/rest/RaidSchema.ts";
import {Avatar, Button, Card, Input, List, message, Modal, Skeleton, Tag, Tooltip} from "antd";
import type {InputRef} from "antd";
import {FieldTimeOutlined, PauseCircleOutlined, PlusCircleOutlined, StopOutlined} from "@ant-design/icons";
import {IconText} from "@/components/IconText.tsx";
import Strav from "@/assets/resources/stravidium.webp";
import Titan from "@/assets/resources/titanium.webp";
import Spice from "@/assets/resources/spice-sand.webp";
import {useEffect, useRef, useState} from "react";
import {localizedNumberFormatter} from "@/helpers/numberFormatHelper.tsx";
import {useStartTimeTrackParticipant, useStopTimeTrackParticipant} from "@/raids/rest/RaidQueries.ts";
import type {AxiosError} from "axios";
import {hasNotStarted, isPausing, isTracking, totalMinutes} from "@/raids/raidDetail/participantIntervalHelper.ts";
import {ParticipantResourceRetriever} from "@/raids/raidDetail/ParticipantResourceRetriever.tsx";

type Props = {
  raid: Raid | null,
  isLoading: boolean,
  isSaving: boolean,
  isEditAble: boolean,
  callBackAddParticipant: (participant: Participant) => void,
  callBackRemoveParticipant: (id: Participant['id']) => void,
  onFocusInput: (inputKey: string) => void,
  onTimeTrackChanged?: (updatedRaid: Raid) => void,
}

export function RaidParticipant({
                                  raid,
                                  isLoading,
                                  isSaving,
                                  isEditAble,
                                  callBackRemoveParticipant,
                                  callBackAddParticipant,
                                  onFocusInput,
                                  onTimeTrackChanged,
                                }: Props) {

  const [msgApi, contextHolder] = message.useMessage();
  const participantNameInputRef = useRef<InputRef>(null);
  const restoreParticipantInputFocusAfterSaveRef = useRef(false);
  const [participants, setParticipants] = useState<Participant[]>(raid?.participants ?? []);
  const [participantName, setParticipantName] = useState("");

  const stopTimeTrackParticipant = useStopTimeTrackParticipant(
      {
        onSuccess: (updatedRaid) => {
          onTimeTrackChanged?.(updatedRaid);
          void msgApi.success("Zeiterfassung gestoppt");
        },
        onError: (err: unknown) => {
          const ax = err as AxiosError;
          void msgApi.error(`Zeiterfassung stoppen fehlgeschlagen: ${ax?.message ?? `Unbekannter Fehler`}`)
        },
      }
  );

  const startTimeTrackParticipant = useStartTimeTrackParticipant(
      {
        onSuccess: (updatedRaid) => {
          onTimeTrackChanged?.(updatedRaid);
          void msgApi.success("Zeiterfassung gestartet");
        },
        onError: (err: unknown) => {
          const ax = err as AxiosError;
          void msgApi.error(`Zeiterfassung starten fehlgeschlagen: ${ax?.message ?? `Unbekannter Fehler`}`);
        },
      }
  );


  const skeletonData = [
    {
      id: 1,
      name: '_',
      spice: 0,
      titanium: 0,
      stravidium: 0,
    },
    {
      id: 2,
      name: '_',
      spice: 0,
      titanium: 0,
      stravidium: 0,
    },
    {
      id: 3,
      name: '_',
      spice: 0,
      titanium: 0,
      stravidium: 0,
    }
  ] as Participant[];

  useEffect(() => {
    if (!raid) return;
    setParticipants(raid.participants);
    setParticipantName("");
  }, [raid]);

  useEffect(() => {
    if (isSaving) return;
    if (!restoreParticipantInputFocusAfterSaveRef.current) return;
    restoreParticipantInputFocusAfterSaveRef.current = false;
    requestAnimationFrame(() => {
      participantNameInputRef.current?.focus({ preventScroll: true });
    });
  }, [isSaving]);

  if (!raid) {
    return null;
  }

  const canAddParticipants = isEditAble && raid.status !== "CLOSED";

  function addParticipant() {
    if (isSaving) return;
    if (!raid || raid.status === "CLOSED") return;
    if (!participantName || participantName.length === 0) return;
    const newParticipant = {
      name: participantName,
      resourcesRetrieved: false,
      intervals: [] as ParticipationInterval[]
    } as Participant;
    restoreParticipantInputFocusAfterSaveRef.current = true;
    callBackAddParticipant(newParticipant);
    setParticipantName("");
  }

  function handleStopTimeTrack(participantId: Participant['id']) {
    if (!raid) return;
    stopTimeTrackParticipant.mutate({raidId: raid.id, participantId: participantId});
  }

  function handleStartTimeTrack(participantId: Participant['id']) {
    if (!raid) return;
    startTimeTrackParticipant.mutate({raidId: raid.id, participantId: participantId});
  }

  return (
      <Card title={"Teilnehmer"}
            className={
              "flex min-h-[400px] min-w-0 flex-none flex-col overflow-hidden " +
              "md:min-h-0 md:flex-1 md:h-full"
            }
            classNames={{
              body: "flex min-h-0 flex-1 flex-col overflow-hidden",
            }}
      >
        {contextHolder}
        <div className={"grid min-h-0 flex-1 grid-rows-[auto_1fr] gap-2"}>
          <div className={"w-full shrink-0 [&_.ant-input-group-addon]:p-0!"}>
            {isLoading && <Skeleton.Input className={"w-full! block!"} active/>}
            {!isLoading && canAddParticipants && <Input
                ref={participantNameInputRef}
                key={"add-participant"}
                data-focus-key={"add-participant"}
                data-tour="add-participant"
                disabled={isSaving}
                value={participantName}
                placeholder={"Spielername"}
                onChange={(e) => {
                  setParticipantName(e.target.value);
                }}
                onPressEnter={() => {
                  if (!isSaving) addParticipant();
                }}
                onFocus={() => {
                  onFocusInput("add-participant");
                }}
                addonAfter={
                  <Button color="default"
                          disabled={isSaving}
                          className={"h-7!"}
                          variant="text"
                          icon={<PlusCircleOutlined/>}
                          loading={isSaving}
                          onClick={() => {
                            addParticipant();
                          }}
                  />
                }
            />
            }
          </div>
          <div className={"min-h-0 overflow-hidden"}>
            <div
                className={"max-h-full min-h-0 h-full overflow-y-auto border border-[var(--ant-color-bg-base)] bg-[var(--ant-color-bg-container)]"}>
              <List
                  className={"bg-transparent"}
                  locale={{
                    emptyText: "Keine Teilnehmer"
                  }}
                  itemLayout="horizontal"
                  dataSource={isLoading ? skeletonData : participants}
                  renderItem={(item) => (
                      <List.Item className={"p-2!"}
                                 actions={
                                   !isLoading ? [
                                     isEditAble &&
                                     raid.status === "RUNNING" &&
                                     isTracking(item) && (
                                         <Tooltip title={"Zeitmessung stoppen"}>
                                           <Button data-tour="time-toggle" color={"default"} variant="filled" icon={<PauseCircleOutlined/>} onClick={() => {
                                             handleStopTimeTrack(item.id);
                                           }}> Zeit stoppen</Button>
                                         </Tooltip>
                                     ),
                                     isEditAble &&
                                     raid.status === "RUNNING" &&
                                     (isPausing(item) || hasNotStarted(item)) && (
                                         <Tooltip title={"Zeitmessung starten"}>
                                           <Button data-tour="time-toggle" color={"default"} variant="filled" icon={<FieldTimeOutlined/>} onClick={() => {
                                             handleStartTimeTrack(item.id);
                                           }}>Zeit starten</Button>
                                         </Tooltip>
                                     ),
                                     (isEditAble && raid.status !== "CLOSED" &&
                                         <Tooltip title={"Teilnehmer entfernen"}>
                                           <Button data-tour="remove-participant" color={"default"} variant="filled" icon={<StopOutlined/>} onClick={() => {
                                             Modal.confirm({
                                               title: "Teilnehmer entfernen",
                                               content: `„${item.name}" wirklich aus dem Raid entfernen?`,
                                               okText: "Entfernen",
                                               okButtonProps: { danger: true },
                                               cancelText: "Abbrechen",
                                               onOk: () => callBackRemoveParticipant(item.id),
                                             });
                                           }}/>
                                         </Tooltip>
                                     ),
                                   ].filter(Boolean) : []
                                 }
                      >
                        <Skeleton loading={!isSaving && isLoading} active avatar={{
                          shape: "circle"
                        }} paragraph={false}>
                          <List.Item.Meta
                              avatar={<Avatar/>}
                              title={item.name}
                              description={
                                <div className={"flex gap-4 flex-wrap"}>
                                  {raid?.status !== "CLOSED" ?
                                      <Tag color="default" className={"text-[var(--ant-color-text-disabled)]!"}>Ergebnisse nach Raidende</Tag>
                                      :
                                      <>
                                        <Tag color="processing" className={"inline-flex items-center leading-none py-1!"}>
                                          {totalMinutes(item).toFixed(0)} Minuten
                                        </Tag>
                                        <IconText image={Strav} text={(item.stravidium ? localizedNumberFormatter(item.stravidium) : "")} key="list-strav"/>
                                        <IconText image={Titan} text={(item.titanium ? localizedNumberFormatter(item.titanium) : "")} key="list-titan"/>
                                        <IconText image={Spice} text={(item.spice ? localizedNumberFormatter(item.spice) : "")} key="list-spice"/>
                                        {isEditAble ? (
                                            <ParticipantResourceRetriever id={item.id} retrieved={item.resourcesRetrieved} />
                                        ) : (
                                            item.resourcesRetrieved && <Tag color="success">Ressourcen erhalten</Tag>
                                        )}
                                      </>
                                  }
                                </div>
                              }
                          />
                        </Skeleton>
                      </List.Item>
                  )}
              />
            </div>
          </div>
        </div>
      </Card>
  )
}